import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthActions } from '@interacta-shared/data-access-auth';
import { LocalStorageService } from '@interacta-shared/data-access-common';
import { isDefined } from '@interacta-shared/util';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    EMPTY,
    exhaustMap,
    filter,
    forkJoin,
    map,
    mergeMap,
    of,
    switchMap,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs';
import {
    NotificationEvent,
    userChannelConfig,
} from '../../models/notification/notification.model';
import { NOTIFICATION_CONFIG } from '../../providers/notification.providers';
import { FirebaseChannelService } from '../../services/firebase-channel.service';
import { FirebaseTransportService } from '../../services/firebase-transport.service';
import { HtmlNotificationService } from '../../services/html-notification.service';
import { NotificationService } from '../../services/notification.service';
import { selectChannelValueLastUpdateTimestamp } from '../firebase-channel/firebase-channel.selectors';
import {
    fetchNotificationsStreamError,
    fetchNotificationsStreamPageSuccess,
    fetchNotificationsStreamSuccess,
    markAllNotificationsAsReadError,
    markAllNotificationsAsReadSuccess,
    markNotificationAsReadError,
    markNotificationAsReadSuccess,
} from './notification-api.actions';
import {
    fetchNotificationsStream,
    fetchNotificationsStreamPage,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    openNotificationDetail,
    setLastUpdateTimestamp,
    setOnlyUnreadNotificationFilter,
} from './notification.actions';
import {
    selectNotificationFeature,
    selectOnlyUnread,
} from './notification.selectors';

// A local storage key shared among all apps
const ONLY_UNREAD_STORAGE_KEY = 'onlyUnreadNotification';
const PAGE_SIZE = 20;

@Injectable()
export class NotificationEffects {
    private readonly actions = inject(Actions);
    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly notificationConfig = inject(NOTIFICATION_CONFIG);
    private readonly firebaseChannelService = inject(FirebaseChannelService);
    private readonly firebaseTransportService = inject(
        FirebaseTransportService,
    );
    private readonly notificationService = inject(NotificationService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly htmlNotificationService = inject(HtmlNotificationService);

    private readonly userNotificationsLastUpdateTimestamp$ = this.store
        .select(
            selectChannelValueLastUpdateTimestamp(
                userChannelConfig.name,
                userChannelConfig.property.notifications,
            ),
        )
        .pipe(filter(isDefined), distinctUntilChanged());

    connectUserChannel$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(AuthActions.login),
                tap(() =>
                    this.firebaseChannelService.connect(
                        this.notificationConfig.endpoints.userChannel,
                        userChannelConfig.name,
                    ),
                ),
            ),
        { dispatch: false },
    );

    afterLogout$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(AuthActions.logout),
                tap(() => {
                    this.firebaseChannelService.disconnectAll();
                    this.firebaseTransportService.closeChannelClientTransport();
                }),
            ),
        { dispatch: false },
    );

    userNotificationsLastUpdateTimestampChanged$ = createEffect(() =>
        this.userNotificationsLastUpdateTimestamp$.pipe(
            tap((lastUpdateTimestamp) =>
                console.debug(
                    'Firebase User channel notifications lastUpdateTimestamp',
                    lastUpdateTimestamp,
                ),
            ),
            map((lastUpdateTimestamp) =>
                setLastUpdateTimestamp({ lastUpdateTimestamp }),
            ),
        ),
    );

    setLastUpdateTimestamp$ = createEffect(() =>
        this.actions.pipe(
            ofType(setLastUpdateTimestamp),
            concatMap((action) =>
                of(action).pipe(
                    withLatestFrom(
                        this.store.select(selectNotificationFeature),
                    ),
                ),
            ),
            map(([, { nextSyncToken, onlyUnread }]) =>
                fetchNotificationsStream({
                    syncToken: nextSyncToken,
                    onlyUnread,
                }),
            ),
        ),
    );

    fetchNotificationsStream$ = createEffect(() =>
        this.actions.pipe(
            ofType(fetchNotificationsStream),
            switchMap(({ syncToken, onlyUnread }) =>
                forkJoin([
                    this.notificationService
                        .fetchNotificationsStream(
                            syncToken,
                            null,
                            onlyUnread,
                            PAGE_SIZE,
                        )
                        .pipe(
                            mergeMap((stream) => {
                                if (
                                    syncToken != null &&
                                    stream.notifications.list.length ===
                                        PAGE_SIZE
                                ) {
                                    // if new notifications are more than a single page, reload first page from scratch
                                    return this.notificationService
                                        .fetchNotificationsStream(
                                            null,
                                            null,
                                            onlyUnread,
                                            PAGE_SIZE,
                                        )
                                        .pipe(
                                            map((stream) => ({
                                                stream,
                                                replace: true,
                                                fetchNew: syncToken != null,
                                            })),
                                        );
                                } else {
                                    if (
                                        syncToken != null &&
                                        stream.notifications.list.length > 1 &&
                                        this.notificationConfig.provider ===
                                            'micronaut'
                                    ) {
                                        stream.notifications.list.reverse();
                                    }

                                    return of({
                                        stream,
                                        replace: false,
                                        fetchNew: syncToken != null,
                                    });
                                }
                            }),
                        ),
                    this.notificationService.getUnreadNotificationsCount(),
                ]).pipe(
                    map(([{ stream, replace, fetchNew }, unreadCount]) =>
                        fetchNotificationsStreamSuccess({
                            stream,
                            unreadCount,
                            replace,
                            fetchNew,
                        }),
                    ),
                    catchError((error) =>
                        of(fetchNotificationsStreamError({ error })),
                    ),
                ),
            ),
        ),
    );

    fetchNotificationsStreamPage$ = createEffect(() =>
        this.actions.pipe(
            ofType(fetchNotificationsStreamPage),
            exhaustMap(({ pageToken, onlyUnread }) =>
                this.notificationService
                    .fetchNotificationsStream(
                        null,
                        pageToken,
                        onlyUnread,
                        PAGE_SIZE,
                    )
                    .pipe(
                        takeUntil(
                            this.actions.pipe(ofType(fetchNotificationsStream)),
                        ),
                        map((stream) =>
                            fetchNotificationsStreamPageSuccess({ stream }),
                        ),
                        catchError((error) =>
                            of(fetchNotificationsStreamError({ error })),
                        ),
                    ),
            ),
        ),
    );

    initializeOnlyUnreadNotification$ = createEffect(() =>
        this.actions.pipe(
            ofType(AuthActions.login),
            map(() => {
                const onlyUnread =
                    this.localStorageService.getEntry<boolean>(
                        ONLY_UNREAD_STORAGE_KEY,
                    ) ?? false;
                return setOnlyUnreadNotificationFilter({
                    onlyUnread,
                    skipFetch: true,
                });
            }),
        ),
    );

    setOnlyUnreadNotificationFilter$ = createEffect(() =>
        this.actions.pipe(
            ofType(setOnlyUnreadNotificationFilter),
            tap(({ onlyUnread }) =>
                this.localStorageService.setEntry(
                    ONLY_UNREAD_STORAGE_KEY,
                    onlyUnread,
                ),
            ),
            filter(({ skipFetch }) => !skipFetch),
            map(({ onlyUnread }) =>
                fetchNotificationsStream({ syncToken: null, onlyUnread }),
            ),
        ),
    );

    markNotificationAsRead$ = createEffect(() =>
        this.actions.pipe(
            ofType(markNotificationAsRead),
            filter(({ notification }) => !notification.read),
            mergeMap(({ notification }) =>
                this.notificationService
                    .markNotificationAsRead(notification.id)
                    .pipe(
                        map(() =>
                            markNotificationAsReadSuccess({
                                notificationId: notification.id,
                            }),
                        ),
                        catchError((error) =>
                            of(markNotificationAsReadError({ error })),
                        ),
                    ),
            ),
        ),
    );

    markAllNotificationsAsRead$ = createEffect(() =>
        this.actions.pipe(
            ofType(markAllNotificationsAsRead),
            concatLatestFrom(() =>
                this.store.select(selectNotificationFeature),
            ),
            mergeMap(([, { notifications, unreadNotificationsCount }]) =>
                notifications.list.length
                    ? this.notificationService
                          .markAllNotificationsAsRead(notifications.list[0].id)
                          .pipe(
                              map(() => markAllNotificationsAsReadSuccess()),
                              catchError((error) =>
                                  of(
                                      markAllNotificationsAsReadError({
                                          error,
                                          previousList: notifications.list,
                                          previousUnreadCount:
                                              unreadNotificationsCount,
                                      }),
                                  ),
                              ),
                          )
                    : EMPTY,
            ),
        ),
    );

    markAllNotificationsAsReadSuccess$ = createEffect(() =>
        this.actions.pipe(
            ofType(markAllNotificationsAsReadSuccess),
            concatLatestFrom(() => this.store.select(selectOnlyUnread)),
            filter(([, onlyUnread]) => onlyUnread),
            map(() =>
                fetchNotificationsStream({ syncToken: null, onlyUnread: true }),
            ),
        ),
    );

    markNotificationAsReadOnOpen$ = createEffect(() =>
        this.actions.pipe(
            ofType(openNotificationDetail),
            map(({ notification }) => markNotificationAsRead({ notification })),
        ),
    );

    openNotificationDetail$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(openNotificationDetail),
                filter(({ navigate }) => navigate),
                tap(({ notification }) => {
                    if (notification.routerLink)
                        this.router.navigate(notification.routerLink, {
                            queryParams: notification.queryParams,
                        });
                }),
            ),
        { dispatch: false },
    );

    openHtmlNotifications$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(fetchNotificationsStreamSuccess),
                filter(({ fetchNew }) => fetchNew),
                tap(({ stream }) =>
                    stream.notifications.list
                        .filter(({ pushEnabled, read }) => !read && pushEnabled)
                        .forEach((notification) => {
                            const openNotificationCallback = (
                                data: NotificationEvent,
                            ) =>
                                this.store.dispatch(
                                    openNotificationDetail({
                                        notification: data,
                                        navigate: true,
                                    }),
                                );

                            this.htmlNotificationService.open(
                                notification.descriptionPlainText,
                                notification.title,
                                notification.id.toString(),
                                notification,
                                openNotificationCallback,
                            );
                        }),
                ),
            ),
        { dispatch: false },
    );

    logErrors$ = createEffect(
        () =>
            this.actions.pipe(
                ofType(
                    fetchNotificationsStreamError,
                    markNotificationAsReadError,
                    markAllNotificationsAsReadError,
                ),
                tap((action) => console.error(`${action.type}`, action.error)),
            ),
        { dispatch: false },
    );
}
